import { breadcrumbJsonLd, buildBreadcrumb } from 'utils/breadcrumb'
import {
    WEBSITE_ID,
    localBusinessNode,
    organizationNode,
    websiteNode,
    buildServiceNode,
} from 'utils/local-business-schema'

/**
 * JSON-LD @graph for the transactional aircon pages (gas top up, installation,
 * general servicing, annual package, condenser cleaning, cassette, repair,
 * near-me, parts replacement and the brand pages).
 *
 * These were all marked up as a single BlogPosting - an article - even though
 * each one is a page selling a service, with a booking form. Now they declare
 * what they are: a WebPage whose main entity is a Service provided by the one
 * business node, plus the Organization and WebSite those nodes point at (the
 * #organization @id used to be referenced but never defined).
 *
 * Offers are only emitted when the page passes a priceRange it actually shows.
 * Several pages used to carry a range copied from general servicing - "Aircon
 * Repair" advertised S$16-S$25 - which is worse than no offer at all.
 *
 * No aggregateRating: see utils/local-business-schema.js. FAQPage is emitted
 * separately by components/commons/Faq.js.
 *
 * @param {object} opts
 * @param {string} opts.path          e.g. "/sg/aircon-servicing-singapore/daikin"
 * @param {string} opts.pageName      the <title>
 * @param {string} opts.description   the meta description
 * @param {string} opts.serviceName
 * @param {string} [opts.serviceType]
 * @param {string} [opts.priceRange]  "S$77.00 - S$112.00", only if shown on the page
 * @param {string} [opts.image]
 * @param {string} opts.datePublished
 * @param {string} opts.modifiedDate  from CONTENT_REVIEWED, never the build time
 * @param {string} [opts.breadcrumbName] leaf label, defaults to serviceName
 */
export const buildAirconServiceGraph = ({
    path,
    pageName,
    description,
    serviceName,
    serviceType = 'Aircon Servicing',
    priceRange,
    image,
    datePublished,
    modifiedDate,
    breadcrumbName,
}) => {
    const trail = buildBreadcrumb(path, breadcrumbName || serviceName)
    const pageUrl = trail[trail.length - 1].url

    const webPage = {
        '@type': 'WebPage',
        '@id': `${pageUrl}/#webpage`,
        url: pageUrl,
        name: pageName,
        description,
        inLanguage: 'en-SG',
        isPartOf: { '@id': WEBSITE_ID },
        breadcrumb: { '@id': `${pageUrl}/#breadcrumblist` },
        mainEntity: { '@id': `${pageUrl}/#service` },
        datePublished,
        dateModified: modifiedDate,
    }
    if (image) webPage.primaryImageOfPage = { '@type': 'ImageObject', url: image }

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPage,
            breadcrumbJsonLd(trail, pageUrl),
            buildServiceNode({ pageUrl, name: serviceName, description, serviceType, priceRange, image }),
            localBusinessNode,
            organizationNode,
            websiteNode,
        ],
    }
}
